import {watch} from 'vue';
import {storeToRefs} from 'pinia';
import {useRoute, useRouter} from 'vue-router';
import {defineNuxtPlugin, useAuth, useRuntimeConfig, useUniversalFetch} from '#imports';

interface SentryConfig {
    dsn: string;
    environment: string;
    application: string;
}

export default defineNuxtPlugin({
    parallel: true,
    dependsOn: ['backend-api-cookies', 'fetch-auth'],
    enforce: 'post',
    async setup(nuxtApp) {
        const runtime = useRuntimeConfig();

        const application = runtime.public.application;
        const {captureConsoleLevels, excludedRouteNames} = runtime.public['@malt/nuxt-sentry-module'];

        const route = !runtime.public.isMF ? useRoute() : undefined;
        const router = !runtime.public.isMF ? useRouter() : undefined;

        try {
            // route can be undefined if no router is used (e.g. in Micro-Frontends)
            const currentRoute = route?.name?.toString();
            const isCurrentRouteExcluded = currentRoute ? excludedRouteNames.includes(currentRoute) : false;

            if (!isCurrentRouteExcluded) {
                const config = await useUniversalFetch<SentryConfig>('/api/sentry/config');

                if (config && config.dsn) {
                    const authStore = useAuth();

                    const {setUpAlerting, teardownAlerting, setUser} = await import('@malt/alerting-vue3/src/configuration');
                    const {user} = storeToRefs(authStore);

                    // Bind `window.captureException` function to `console.error` before `setUpAlerting` is called
                    // eslint-disable-next-line no-console
                    window.captureException = console.error;

                    // Setup without user because it's not ready
                    setUpAlerting({
                        configuration: {
                            dsn: config.dsn,
                            environment: config.environment,
                            application: application || config.application,
                            captureConsoleLevels,
                        },
                        app: nuxtApp.vueApp,
                        router,
                    });

                    // Set the user when it's ready
                    watch(user, (newUser) => {
                        if (newUser) {
                            const selectedIdentity = newUser.identities?.find((identity) => identity.selected);
                            const sentryUser = {
                                accountId: newUser.id,
                                identityId: selectedIdentity?.id ?? '',
                                email: newUser.email,
                            };
                            setUser({
                                connectedUser: sentryUser,
                            });
                        }
                    });

                    nuxtApp.vueApp.onUnmount(() => {
                        teardownAlerting();
                    });
                }
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Unable to load sentry configuration', error);
        }
    },
});
